import React, {useState, useCallback, type ReactNode} from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  FormControl,
  Select,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import {Link as RouterLink} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {SCAN_LIBRARY} from '../../graphql/mutations';
import {GET_EVAL_PROMPTS, GET_EVAL_REPORTS, GET_OPENROUTER_MODELS} from '../../graphql/queries';
import {formatCost} from '../../utils/format';
import {EVAL_MODELS} from '../../data/evalModels';

interface ScanLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  orgId: string | undefined;
  /** Total source items in the library (for cost estimation) */
  totalItems?: number;
  onSnackbar: (msg: {open: boolean; message: string; severity: 'success' | 'error' | 'info'; action?: ReactNode}) => void;
}

const OR_MODEL_IDS = EVAL_MODELS;


export default function ScanLibraryDialog({open, onClose, orgId, totalItems, onSnackbar}: ScanLibraryDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmReset, setConfirmReset] = useState(false);
  const [sampleSize, setSampleSize] = useState<string>('');
  const [promptName, setPromptName] = useState<string>('');
  const [modelName, setModelName] = useState<string>('meta-llama/llama-3.1-8b-instruct');
  const [scanLibrary] = useMutation(SCAN_LIBRARY);
  const {data: promptsData} = useQuery(GET_EVAL_PROMPTS);
  const prompts = promptsData?.evalPrompts || [];

  // Pricing + avg tokens for cost estimation
  const {data: orModelsData} = useQuery(GET_OPENROUTER_MODELS, {variables: {ids: OR_MODEL_IDS}, skip: !open});
  const {data: reportsData} = useQuery(GET_EVAL_REPORTS, {skip: !open});

  const costEstimate = React.useMemo(() => {
    const hasWork = selected.size > 0;
    if (!hasWork) return null;

    const itemCount = sampleSize ? parseInt(sampleSize, 10) || 0 : (totalItems || 0);
    if (itemCount === 0) return null;

    // Get average tokens per document from eval reports
    const reports = (reportsData?.evalReports || []) as any[];
    const withTokens = reports.filter((r: any) => r.inputTokens > 0 && r.totalCases > 0);
    // Per-case average (eval runs N test cases, so tokens/totalCases ≈ tokens per doc)
    const avgInputPerDoc = withTokens.length > 0
      ? withTokens.reduce((sum: number, r: any) => sum + r.inputTokens / r.totalCases, 0) / withTokens.length
      : 500; // fallback: ~500 input tokens per doc
    const avgOutputPerDoc = withTokens.length > 0
      ? withTokens.reduce((sum: number, r: any) => sum + (r.outputTokens || 0) / r.totalCases, 0) / withTokens.length
      : 200; // fallback

    const totalInput = avgInputPerDoc * itemCount;
    const totalOutput = avgOutputPerDoc * itemCount;

    // OpenRouter pricing
    const pricing = (orModelsData?.openRouterModels || []).find((m: any) => m.id === modelName);
    if (!pricing) return {cost: null, itemCount, avgInputPerDoc: Math.round(avgInputPerDoc), avgOutputPerDoc: Math.round(avgOutputPerDoc)};

    const cost = (totalInput / 1_000_000) * pricing.inputPer1M + (totalOutput / 1_000_000) * pricing.outputPer1M;
    return {cost, itemCount, avgInputPerDoc: Math.round(avgInputPerDoc), avgOutputPerDoc: Math.round(avgOutputPerDoc)};
  }, [selected, sampleSize, totalItems, modelName, reportsData, orModelsData]);

  const toggle = useCallback((key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setConfirmReset(false);
  }, []);

  const handleStart = useCallback(async () => {
    const isFullReset = selected.has('reset-all');

    // Full reset confirmation gate
    if (isFullReset && !confirmReset) {
      setConfirmReset(true);
      return;
    }

    const tasks: string[] = [];
    const resetTasksList: string[] = [];

    if (isFullReset) {
      // Reset only — don't auto-reprocess unless user also checked individual tasks
      resetTasksList.push('analysis', 'extraction');
    } else {
      if (selected.has('analyze-new') || selected.has('re-analyze')) tasks.push('analysis');
      if (selected.has('extract-new') || selected.has('re-extract')) tasks.push('extraction');
      if (selected.has('re-analyze')) resetTasksList.push('analysis');
      if (selected.has('re-extract')) resetTasksList.push('extraction');
    }

    const parsedSample = sampleSize ? parseInt(sampleSize, 10) : undefined;
    const validSample = parsedSample && parsedSample > 0 ? parsedSample : undefined;

    try {
      console.log('[ScanLibraryDialog] Submitting with modelName:', modelName);
      const result = await scanLibrary({
        variables: {
          orgId,
          tasks,
          resetStatuses: resetTasksList.length > 0,
          resetTasks: resetTasksList.length > 0 ? resetTasksList : undefined,
          ...(validSample ? {sampleSize: validSample} : {}),
          ...(promptName ? {promptName} : {}),
          modelName,
        },
      });
      const jobId = result.data?.scanLibrary?.id;
      const sampleLabel = validSample ? ` (random ${validSample})` : '';
      const message = isFullReset && tasks.length === 0
        ? `Library reset started${sampleLabel}`
        : isFullReset
        ? `Library reset + re-process started${sampleLabel}`
        : `Library scan started (${Array.from(selected).map(k => ({
            'analyze-new': 'analyze new', 're-analyze': 're-analyze all',
            'extract-new': 'extract new', 're-extract': 're-extract all',
          }[k])).filter(Boolean).join(', ')})${sampleLabel}`;
      onSnackbar({
        open: true, severity: 'success', message,
        action: jobId ? <Button color="inherit" size="small" component={RouterLink} to={`/org/jobs/${jobId}`} sx={{textTransform: 'none', fontWeight: 600}}>View job</Button> : undefined,
      });
      onClose();
      setConfirmReset(false);
    } catch (err: any) {
      onSnackbar({open: true, severity: 'error', message: err.message});
    }
  }, [selected, scanLibrary, orgId, onSnackbar, onClose, confirmReset, sampleSize]);

  const isFullReset = selected.has('reset-all');

  const options: {key: string; label: string; description: string; disabledBy?: string}[] = [
    {key: 'analyze-new', label: 'Analyze new items', description: 'Summarize and classify items not yet analyzed', disabledBy: 're-analyze'},
    {key: 're-analyze', label: 'Re-analyze all items', description: 'Reset and re-analyze every item (clears manual overrides)'},
    {key: 'extract-new', label: 'Extract new items', description: 'Extract text from process items not yet extracted', disabledBy: 're-extract'},
    {key: 're-extract', label: 'Re-extract all items', description: 'Reset and re-extract all process items'},
  ];

  return (
    <Dialog open={open} onClose={() => { onClose(); setConfirmReset(false); }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>Scan Library</DialogTitle>
      <DialogContent>
        <Typography sx={{fontSize: 13, color: '#6B7280', mb: 2}}>
          Choose which operations to run on your source library.
        </Typography>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
          {options.map(opt => {
            const disabled = opt.disabledBy ? selected.has(opt.disabledBy) : false;
            return (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    checked={selected.has(opt.key)}
                    disabled={disabled}
                    onChange={() => toggle(opt.key)}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography sx={{fontSize: 14, color: disabled ? '#9CA3AF' : undefined}}>{opt.label}</Typography>
                    <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>{opt.description}</Typography>
                  </Box>
                }
              />
            );
          })}
        </Box>

        {/* Sample size */}
        <Divider sx={{my: 1.5}} />
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <TextField
            size="small"
            type="number"
            placeholder="All items"
            value={sampleSize}
            onChange={(e) => setSampleSize(e.target.value)}
            inputProps={{min: 1}}
            sx={{
              width: 100,
              '& .MuiInputBase-input': {fontSize: 13, py: 0.75},
            }}
          />
          <Box>
            <Typography sx={{fontSize: 14}}>Random sample</Typography>
            <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
              Run on a random subset instead of the full library. Leave blank for all.
            </Typography>
          </Box>
        </Box>

        {/* Analysis prompt (only if prompts exist) */}
        {prompts.length > 0 && (
          <>
            <Divider sx={{my: 1.5}} />
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
              <FormControl size="small" sx={{minWidth: 160}}>
                <Select
                  native
                  value={promptName}
                  onChange={(e) => setPromptName(e.target.value as string)}
                  sx={{fontSize: 13, '& .MuiNativeSelect-select': {py: 0.75}}}
                >
                  <option value="">current (built-in)</option>
                  {prompts.filter((p: any) => p.name !== 'current').map((p: any) => (
                    <option key={p.name} value={p.name}>{p.displayName || p.name}{p.lastEvalScore ? ` (${p.lastEvalScore.weightedScore ?? p.lastEvalScore.guideableAccuracy ?? p.lastEvalScore.accuracy}%)` : ''}</option>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Typography sx={{fontSize: 14}}>Analysis prompt</Typography>
                <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
                  Choose which prompt to use for analysis. Manage prompts in Prompt Eval.
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Analysis model (always visible) */}
        <Divider sx={{my: 1.5}} />
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <FormControl size="small" sx={{minWidth: 200}}>
            <Select
              native
              value={modelName}
              onChange={(e) => { console.log('[ScanLibraryDialog] Model onChange:', e.target.value); setModelName(e.target.value as string); }}
              sx={{fontSize: 13, '& .MuiNativeSelect-select': {py: 0.75}}}
            >
              {OR_MODEL_IDS.map(id => {
                const p = (orModelsData?.openRouterModels || []).find((m: any) => m.id === id);
                return <option key={id} value={id}>{p?.name || id.split('/').pop()}</option>;
              })}
            </Select>
          </FormControl>
          <Box>
            <Typography sx={{fontSize: 14}}>Analysis model</Typography>
            <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
              Choose which LLM model to use for analysis.
            </Typography>
          </Box>
        </Box>

        {/* Reset to fresh import */}
        <Divider sx={{my: 1.5}} />
        <FormControlLabel
          control={
            <Checkbox
              checked={isFullReset}
              onChange={() => {
                setSelected(prev => {
                  const next = new Set<string>();
                  if (!prev.has('reset-all')) next.add('reset-all');
                  return next;
                });
                setConfirmReset(false);
              }}
              size="small"
              sx={{'&.Mui-checked': {color: '#EF4444'}}}
            />
          }
          label={
            <Box>
              <Typography sx={{fontSize: 14, color: isFullReset ? '#991B1B' : undefined, fontWeight: isFullReset ? 600 : undefined}}>
                Reset to fresh import
              </Typography>
              <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
                Clear all analysis, classification, extraction, and overrides. Use the options above to also re-process.
              </Typography>
            </Box>
          }
        />
        {confirmReset && (
          <Box sx={{mt: 1.5, p: 1.5, bgcolor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA'}}>
            <Typography sx={{fontSize: 13, color: '#991B1B', fontWeight: 500}}>
              This will wipe all analysis results, extracted text, classifications, and manual overrides for every item in the library. Click "Start" again to confirm.
            </Typography>
          </Box>
        )}
        {/* Cost estimate */}
        {costEstimate && (
          <Box sx={{mt: 2, p: 1.5, bgcolor: '#F0F5FF', borderRadius: '8px', border: '1px solid #DBEAFE'}}>
            <Typography sx={{fontSize: 13, fontWeight: 600, color: '#1E40AF', mb: 0.25}}>
              {costEstimate.cost != null
                ? `Estimated cost: ~${formatCost(costEstimate.cost)}`
                : 'Pricing unavailable for this model'}
            </Typography>
            <Typography sx={{fontSize: 11, color: '#6B7280'}}>
              {costEstimate.itemCount} document{costEstimate.itemCount !== 1 ? 's' : ''} · avg ~{costEstimate.avgInputPerDoc} in / ~{costEstimate.avgOutputPerDoc} out tokens per doc
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2}}>
        <Button onClick={() => { onClose(); setConfirmReset(false); }} sx={{textTransform: 'none'}}>Cancel</Button>
        <Button
          variant="contained"
          disabled={selected.size === 0}
          onClick={handleStart}
          color={confirmReset ? 'error' : 'primary'}
          sx={{textTransform: 'none'}}
        >
          {confirmReset ? 'Confirm Reset & Start' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
